import { useAppDispatch } from '@/app/redux/hooks';
import {
  openConfirmationDialogue,
  setConfirmationDialogueCallback,
} from '@/app/redux/slices/modalsSlice';
import {
  Grapheme,
  Morpheme,
  Sentence,
  Word,
} from '@/common/types/scopeAndSequence';
import { ActionLoader } from '@/common/ui/LoadingSpinners';
import {
  getGraphemeById,
  getMorphemesByIds,
  getWordById,
} from '@/common/utils/scopeAndSequence';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  MouseSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import { Command, CommandItem, CommandList } from '@shadcn/components/command';
import { Input } from '@shadcn/components/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { cn } from '@shadcn/lib/utils';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const PAGE_SIZE = 20;

type SelectorItem = Grapheme | Morpheme | Word | Sentence;

type ItemValidation = { reason: string; severity: 'warning' | 'error' };

interface ItemSelectorProps {
  id?: string;
  type: 'grapheme' | 'morpheme' | 'word' | 'sentence';
  items: SelectorItem[];
  selectedItemIds: string[];
  itemValidations?: Record<string, ItemValidation>;
  showMorphemes?: boolean;
  onSelectedItemIdsChange: (selectedItemIds: string[]) => void;
}

function sortItems(searchTerm: string) {
  return (a: SelectorItem, b: SelectorItem) => {
    const term = searchTerm.toLowerCase();
    const aIndex = a.spelling.toLowerCase().indexOf(term);
    const bIndex = b.spelling.toLowerCase().indexOf(term);
    const aPos = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
    const bPos = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
    const aLengthDiff = Math.abs(a.spelling.length - term.length);
    const bLengthDiff = Math.abs(b.spelling.length - term.length);
    return aPos - bPos + aLengthDiff - bLengthDiff;
  };
}

function getSecondaryInfo(
  item: SelectorItem,
  type: 'grapheme' | 'morpheme' | 'word' | 'sentence',
  showMorphemes?: boolean,
): string {
  if (type === 'grapheme') {
    const grapheme = item as Grapheme;
    const example = getWordById(grapheme.example)?.spelling;
    let secondary = `/${grapheme.pronunciation}/`;
    if (example) secondary += ` ${example}`;
    return secondary;
  }
  if (type === 'morpheme') {
    const morpheme = item as Morpheme;
    return morpheme.graphemes
      .map((gId) => getGraphemeById(gId)?.pronunciation ?? '')
      .join('');
  }
  if (type === 'word') {
    const word = item as Word;
    let secondary = word.definition;
    if (showMorphemes) {
      const morphemes = getMorphemesByIds(word.morphemes);
      secondary += ` <${morphemes.map((m) => m.spelling).join(' + ')}>`;
    }
    return secondary;
  }
  return '';
}

interface ChipInfo {
  inlineLabel: string | null;
  tooltipLines: string[];
}

function getChipInfo(
  item: SelectorItem,
  type: 'grapheme' | 'morpheme' | 'word' | 'sentence',
  showMorphemes?: boolean,
): ChipInfo {
  switch (type) {
    case 'grapheme': {
      const grapheme = item as Grapheme;
      const pronunciation = `/${grapheme.pronunciation}/`;
      const example = getWordById(grapheme.example)?.spelling;
      return {
        inlineLabel: pronunciation,
        tooltipLines: [example ? `${pronunciation} ${example}` : pronunciation],
      };
    }
    case 'morpheme': {
      const morpheme = item as Morpheme;
      return {
        inlineLabel: null,
        tooltipLines: [
          morpheme.graphemes
            .map((gId) => getGraphemeById(gId)?.pronunciation ?? '')
            .join(''),
        ],
      };
    }
    case 'word': {
      const word = item as Word;
      const lines = [word.definition];
      if (showMorphemes)
        lines.push(
          getMorphemesByIds(word.morphemes)
            .map((m) => m.spelling)
            .join(' + '),
        );
      return { inlineLabel: null, tooltipLines: lines };
    }
    case 'sentence':
      return { inlineLabel: null, tooltipLines: [] };
  }
}

export default function ItemSelector({
  id = 'item-selector',
  type,
  items,
  selectedItemIds,
  itemValidations,
  showMorphemes,
  onSelectedItemIdsChange,
}: ItemSelectorProps) {
  const inputContainerRef = useRef<HTMLDivElement>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState(items);
  const [isOpen, setIsOpen] = useState(false);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [dragOrder, setDragOrder] = useState(selectedItemIds);

  useEffect(() => {
    if (!activeId) setDragOrder(selectedItemIds);
  }, [selectedItemIds, activeId]);

  // Use selectedItemIds directly for rendering; only use dragOrder during a drag
  const displayOrder = activeId ? dragOrder : selectedItemIds;
  const [limit, setLimit] = useState(20);
  const [visibleItems, setVisibleItems] = useState(
    filteredItems.slice(0, limit),
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const dispatch = useAppDispatch();
  const sensors = useSensors(useSensor(MouseSensor));

  useEffect(() => {
    setVisibleItems(filteredItems.slice(0, limit));
  }, [filteredItems, limit]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredItems(items);
      setLimit(PAGE_SIZE);
      return;
    }
    const terms = searchTerm.toLowerCase().split(' ');
    setFilteredItems(
      items
        .filter((item) =>
          terms.every((term) => item.spelling.toLowerCase().includes(term)),
        )
        .sort(sortItems(searchTerm)),
    );
    setLimit(PAGE_SIZE);
  }, [searchTerm, items]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;
      if (
        !target.closest('#item-selector-dropdown') &&
        !target.closest(`#${id}`)
      ) {
        setIsOpen(false);
        setLimit(PAGE_SIZE);
      }
    }
    setTimeout(
      () => document.addEventListener('click', handleClickOutside),
      100,
    );
    return () => document.removeEventListener('click', handleClickOutside);
  }, [id, isOpen]);

  function handleSelect(itemId: string) {
    onSelectedItemIdsChange([...selectedItemIds, itemId]);
  }

  function handleRemove(index: number) {
    const updated = [...selectedItemIds];
    updated.splice(index, 1);
    onSelectedItemIdsChange(updated);
  }

  function handleDragStart({ active }: DragStartEvent) {
    setActiveId(active.id);
  }

  function handleDragOver({ active, over }: DragOverEvent) {
    const overId = over?.id;
    if (!overId || active.id === overId) return;
    setDragOrder((prev) => {
      const activeIndex = prev.indexOf(active.id as string);
      const overIndex = prev.indexOf(overId as string);
      return arrayMove(prev, activeIndex, overIndex);
    });
  }

  function handleDragEnd(_event: DragEndEvent) {
    onSelectedItemIdsChange(dragOrder);
    setActiveId(null);
  }

  function handleDragCancel() {
    setDragOrder(selectedItemIds);
    setActiveId(null);
  }

  function handleKeyUp(e: React.KeyboardEvent<HTMLInputElement>) {
    e.preventDefault();
    if (!visibleItems.length) return;
    if (e.key === 'Enter') {
      handleSelect(visibleItems[0].id);
      setSearchTerm('');
      setLimit(PAGE_SIZE);
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  }

  function handleClearAll() {
    setConfirmationDialogueCallback(async () => {
      onSelectedItemIdsChange([]);
    });
    dispatch(
      openConfirmationDialogue({
        title: `Clear all ${type}s`,
        description: `Are you sure you want to clear selected ${type}s?`,
        variant: 'destructive',
      }),
    );
  }

  const commandListRef = useRef<HTMLDivElement>(null);

  function handleScroll() {
    if (!commandListRef.current || isLoadingMore) return;
    if (filteredItems.length <= limit) return;
    const { scrollTop, scrollHeight, clientHeight } = commandListRef.current;
    if (scrollHeight - scrollTop <= clientHeight + 20) {
      setIsLoadingMore(true);
      setTimeout(() => {
        setLimit((prev) => Math.min(prev + PAGE_SIZE, filteredItems.length));
        setIsLoadingMore(false);
      }, 100);
    }
  }

  return (
    <ItemSelectorContainer>
      {selectedItemIds.length > 0 && (
        <ClearAllButton onClick={handleClearAll}>
          <XIcon size={16} />
        </ClearAllButton>
      )}
      <SelectedContainer>
        {displayOrder.length > 0 ? (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <SortableContext items={displayOrder}>
              {displayOrder.map((itemId, index) => (
                <ItemChip
                  key={`${itemId}-${index}`}
                  type={type}
                  item={items.find((item) => item.id === itemId)}
                  validation={itemValidations?.[itemId]}
                  showMorphemes={showMorphemes}
                  onRemove={() => handleRemove(index)}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <div className="text-sm text-muted-foreground w-full text-center my-auto">
            No {type}s selected
          </div>
        )}
      </SelectedContainer>
      <div className="relative" ref={inputContainerRef}>
        <MagnifyingGlassIcon
          size={18}
          className="absolute top-1/2 left-2 -translate-y-1/2 z-10 pointer-events-none"
        />
        <Input
          id={id}
          onFocus={() => setIsOpen(true)}
          placeholder={`Search ${type}s...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyUp={handleKeyUp}
          className="pl-8 border-b-0 border-l-0 border-r-0"
          autoComplete="off"
        />
        {isOpen && (
          <OptionsContainer
            id="item-selector-dropdown"
            onWheel={(e) => e.stopPropagation()}
          >
            <Command>
              <CommandList
                ref={commandListRef}
                className="max-h-[200px] overflow-y-auto"
                onScroll={handleScroll}
              >
                {visibleItems.map((item, index) => (
                  <CommandItem
                    key={`${item.id}-${index}`}
                    value={item.id}
                    onSelect={() => {
                      if (items.includes(item)) {
                        handleSelect(item.id);
                      }
                    }}
                  >
                    <span>{item.spelling}</span>
                    <span className="text-xs text-muted-foreground">
                      {getSecondaryInfo(item, type, showMorphemes)}
                    </span>
                  </CommandItem>
                ))}
                {isLoadingMore && (
                  <div className="flex justify-center py-2">
                    <ActionLoader />
                  </div>
                )}
                {limit < filteredItems.length && !isLoadingMore && (
                  <div className="text-center py-2 text-sm text-muted-foreground">
                    Scroll to load more...
                  </div>
                )}
                {filteredItems.length === 0 && (
                  <div className="text-center py-2 text-sm text-muted-foreground">
                    No {type}s found
                  </div>
                )}
              </CommandList>
            </Command>
          </OptionsContainer>
        )}
      </div>
    </ItemSelectorContainer>
  );
}

interface ItemChipProps {
  type: 'grapheme' | 'morpheme' | 'word' | 'sentence';
  item: SelectorItem | undefined;
  validation?: ItemValidation;
  showMorphemes?: boolean;
  onRemove: () => void;
}

function ItemChip({
  type,
  item,
  validation,
  showMorphemes,
  onRemove,
}: ItemChipProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item?.id ?? 'unknown-item' });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  const { inlineLabel, tooltipLines } = item
    ? getChipInfo(item, type, showMorphemes)
    : { inlineLabel: null, tooltipLines: [] };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <ItemChipContainer
          ref={setNodeRef}
          style={style}
          {...attributes}
          {...listeners}
          className={cn(
            'flex items-center gap-2',
            validation?.severity === 'warning' && 'bg-rag-amber/10',
            (validation?.severity === 'error' || !item) && 'bg-red-50',
          )}
        >
          {item ? (
            <span
              className={cn(
                'flex items-baseline gap-1',
                validation?.severity === 'warning' &&
                  'text-amber-500 font-medium',
                validation?.severity === 'error' &&
                  'text-destructive font-medium',
              )}
            >
              {item.spelling}
              {inlineLabel && (
                <span className="text-xs text-muted-foreground">
                  {inlineLabel}
                </span>
              )}
            </span>
          ) : (
            <span className="text-destructive font-medium">Unknown {type}</span>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="size-5"
            onPointerDown={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            <XIcon size={12} />
          </Button>
        </ItemChipContainer>
      </TooltipTrigger>
      {(tooltipLines.length > 0 || validation) && (
        <TooltipContent className="flex flex-col gap-1">
          {item && <strong>{item.spelling}</strong>}
          {tooltipLines.map((line, i) => (
            <div key={i} className={i > 0 ? 'text-muted-foreground' : ''}>
              {line}
            </div>
          ))}
          {validation && (
            <div
              className={
                validation.severity === 'error'
                  ? 'text-destructive'
                  : 'text-amber-500'
              }
            >
              {validation.reason}
            </div>
          )}
        </TooltipContent>
      )}
    </Tooltip>
  );
}

const ItemSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  border: 1px solid hsl(var(--border));
  border-radius: 8px;
`;

const ClearAllButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  color: hsl(var(--muted-foreground));
  z-index: 1;
  &:hover {
    background-color: hsl(var(--muted));
  }
`;

const SelectedContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 8px;
  width: 100%;
  min-height: 52px;
`;

const ItemChipContainer = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  height: 32px;
  gap: 8px;
  padding: 4px 8px;
  border: 1px solid hsl(var(--border));
  border-radius: 8px;
  font-size: 14px;
  line-height: 0.8;
`;

const OptionsContainer = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  border: 1px solid hsl(var(--border));
  border-radius: 6px;
  background-color: hsl(var(--background));
  z-index: 1000;
`;
